import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faDownload,
  faUpload,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { secureFetch } from '../../utils/secureFetch';
import { isEmbedded } from '../../utils/isEmbedded';
import './ProductBulkImportModal.css';

function ProductBulkImportModal({ isOpen, onClose, onImportComplete }) {
  const [hasVariants, setHasVariants] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [activeTab, setActiveTab] = useState('correct');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      setHasVariants(false);
      setUploadedFile(null);
      setValidationResult(null);
      setActiveTab('correct');
      setIsImporting(false);
      setImportProgress(null);
      setErrorMessage('');
      setSuccessMessage('');

      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isImporting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isImporting, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isImporting) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      onClose();
    }
  };

  const handleVariantToggle = (value) => {
    setHasVariants(value);
    setUploadedFile(null);
    setValidationResult(null);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      if (!validTypes.includes(file.type)) {
        setErrorMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
        return;
      }

      setUploadedFile(file);
      setValidationResult(null);
      setErrorMessage('');
      setSuccessMessage('');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = async () => {
    try {
      setErrorMessage('');

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded() 
        ? 'https://api.minymol.com/bulk-import/catalog/template'
        : 'https://api.minymol.com/bulk-import/template';

      const response = await secureFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hasVariants })
      });

      if (!response.ok) {
        throw new Error('Error al generar la plantilla');
      }

      const result = await response.json();

      const byteCharacters = atob(result.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Plantilla descargada exitosamente');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Error al descargar la plantilla. Por favor intenta nuevamente.');
    }
  };

  const handleValidateFile = async () => {
    if (!uploadedFile) return;

    try {
      setIsValidating(true);
      setErrorMessage('');
      setValidationResult(null);

      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('hasVariants', hasVariants.toString());

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded()
        ? 'https://api.minymol.com/bulk-import/catalog/validate'
        : 'https://api.minymol.com/bulk-import/validate';

      const response = await secureFetch(endpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al validar el archivo');
      }

      const result = await response.json();
      setValidationResult(result);

      if (result.errors && result.errors.length > 0) {
        setActiveTab('errors');
        setErrorMessage(`Se encontraron ${result.errors.length} error(es) en el archivo`);
      } else {
        setActiveTab('correct');
        setSuccessMessage(`Validación exitosa: ${result.validProducts.length} producto(s) listo(s) para importar`);
      }
    } catch (error) {
      console.error('Error validating file:', error);
      setErrorMessage(error.message || 'Error al validar el archivo. Por favor intenta nuevamente.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleImportProducts = async () => {
    if (!validationResult || !validationResult.validProducts || validationResult.validProducts.length === 0) {
      return;
    }

    try {
      setIsImporting(true);
      setErrorMessage('');
      setImportProgress({
        current: 0,
        total: validationResult.validProducts.length,
        success: 0,
        failed: 0
      });

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded()
        ? 'https://api.minymol.com/bulk-import/catalog/import'
        : 'https://api.minymol.com/bulk-import/import';

      const response = await secureFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          validProducts: validationResult.validProducts
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al importar productos');
      }

      const result = await response.json();

      const successCount = result.successCount || 0;
      const failedCount = result.failureCount || 0;
      const totalCount = result.totalProcessed || validationResult.validProducts.length;

      setImportProgress({
        current: totalCount,
        total: totalCount,
        success: successCount,
        failed: failedCount
      });

      if (failedCount > 0) {
        setErrorMessage(`Importación completada con ${failedCount} error(es). ${successCount} producto(s) importado(s) exitosamente.`);
      } else {
        setSuccessMessage(`¡Importación exitosa! ${successCount} producto(s) importado(s).`);
      }

      if (onImportComplete) {
        onImportComplete();
      }

      setTimeout(() => {
        if (failedCount === 0) {
          onClose();
        }
      }, 2000);

    } catch (error) {
      console.error('Error importing products:', error);
      setErrorMessage(error.message || 'Error al importar productos. Por favor intenta nuevamente.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="product-bulk-import-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-bulk-import-modal-title"
    >
      <div className="product-bulk-import-modal-backdrop" />

      <div className="product-bulk-import-modal-container">
        <div
          ref={modalRef}
          className="product-bulk-import-modal-content"
          tabIndex="-1"
        >
          {/* Header */}
          <div className="product-bulk-import-modal-header">
            <h2 id="product-bulk-import-modal-title">
              Importar Productos Masivamente
            </h2>
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="product-bulk-import-modal-close"
              aria-label="Cerrar modal"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Content */}
          <div className="product-bulk-import-modal-body">
            {/* Success Message */}
            {successMessage && (
              <div className="product-bulk-import-alert product-bulk-import-alert-success">
                <FontAwesomeIcon icon={faCheckCircle} />
                <p>{successMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="product-bulk-import-alert product-bulk-import-alert-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Variant Toggle */}
            <div className="product-bulk-import-section">
              <label className="product-bulk-import-label">
                Tipo de productos a importar
              </label>
              <div className="product-bulk-import-toggle-group">
                <button
                  onClick={() => handleVariantToggle(false)}
                  disabled={isImporting || isValidating}
                  className={`product-bulk-import-toggle-btn ${!hasVariants ? 'active' : ''}`}
                >
                  <div className="font-medium">Sin variantes</div>
                  <div className="text-sm">Productos simples sin colores ni tallas</div>
                </button>
                <button
                  onClick={() => handleVariantToggle(true)}
                  disabled={isImporting || isValidating}
                  className={`product-bulk-import-toggle-btn ${hasVariants ? 'active' : ''}`}
                >
                  <div className="font-medium">Con variantes</div>
                  <div className="text-sm">Productos con colores y/o tallas</div>
                </button>
              </div>
            </div>

            {/* Download Template Button */}
            <div className="product-bulk-import-section">
              <button
                onClick={handleDownloadTemplate}
                disabled={isImporting || isValidating}
                className="product-bulk-import-btn product-bulk-import-btn-secondary"
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Descargar Plantilla {hasVariants ? 'con Variantes' : 'Simple'}</span>
              </button>
            </div>

            {/* File Upload Area */}
            <div className="product-bulk-import-section">
              <label className="product-bulk-import-label">
                Subir archivo Excel
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isImporting || isValidating}
              />
              <div
                onClick={handleUploadClick}
                className={`product-bulk-import-upload-area ${uploadedFile ? 'has-file' : ''} ${(isImporting || isValidating) ? 'disabled' : ''}`}
              >
                <FontAwesomeIcon
                  icon={uploadedFile ? faCheckCircle : faUpload}
                  className="upload-icon"
                />
                {uploadedFile ? (
                  <div>
                    <p className="font-medium">
                      Archivo seleccionado: {uploadedFile.name}
                    </p>
                    <p className="text-sm">
                      Haz clic para seleccionar otro archivo
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">
                      Haz clic para seleccionar un archivo
                    </p>
                    <p className="text-sm">
                      o arrastra y suelta aquí
                    </p>
                    <p className="text-xs">
                      Formatos soportados: .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Validate Button */}
            {uploadedFile && !validationResult && (
              <div className="product-bulk-import-section">
                <button
                  onClick={handleValidateFile}
                  disabled={isImporting || isValidating}
                  className="product-bulk-import-btn product-bulk-import-btn-primary"
                >
                  {isValidating ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>Validando archivo...</span>
                    </>
                  ) : (
                    <span>Validar Archivo</span>
                  )}
                </button>
              </div>
            )}

            {/* Validation Results */}
            {validationResult && (
              <div className="product-bulk-import-section">
                {/* Tabs */}
                <div className="product-bulk-import-tabs">
                  <button
                    onClick={() => setActiveTab('correct')}
                    className={`product-bulk-import-tab ${activeTab === 'correct' ? 'active' : ''}`}
                  >
                    Correctos ({validationResult.validProducts?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('errors')}
                    className={`product-bulk-import-tab ${activeTab === 'errors' ? 'active' : ''}`}
                  >
                    Errores ({validationResult.errors?.length || 0})
                  </button>
                </div>

                {/* Tab Content */}
                <div className="product-bulk-import-tab-content">
                  {activeTab === 'correct' && (
                    <div className="product-bulk-import-table-wrapper">
                      {validationResult.validProducts && validationResult.validProducts.length > 0 ? (
                        <table className="product-bulk-import-table">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>Categoría</th>
                              <th>Precio</th>
                              {hasVariants && (
                                <>
                                  <th>Color</th>
                                  <th>Talla</th>
                                </>
                              )}
                              <th>Stock</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResult.validProducts.map((product, index) => {
                              const totalStock = product.warehouseStock
                                ? Object.values(product.warehouseStock).reduce((sum, stock) => sum + (stock || 0), 0)
                                : 0;

                              return (
                                <tr key={index}>
                                  <td>{product.name}</td>
                                  <td>{product.category}</td>
                                  <td>${product.price?.toLocaleString()}</td>
                                  {hasVariants && (
                                    <>
                                      <td>{product.color || '-'}</td>
                                      <td>{product.size || '-'}</td>
                                    </>
                                  )}
                                  <td>{product.managesStock ? totalStock : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <div className="product-bulk-import-empty">
                          No hay productos válidos
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'errors' && (
                    <div className="product-bulk-import-table-wrapper">
                      {validationResult.errors && validationResult.errors.length > 0 ? (
                        <table className="product-bulk-import-table">
                          <thead>
                            <tr>
                              <th>Fila</th>
                              <th>Columna</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResult.errors.map((error, index) => (
                              <tr key={index}>
                                <td className="font-medium">{error.row}</td>
                                <td>{error.column}</td>
                                <td className="text-danger">{error.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="product-bulk-import-empty product-bulk-import-empty-success">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <p>¡No se encontraron errores!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="product-bulk-import-modal-footer">
            {importProgress && (
              <div className="product-bulk-import-progress">
                <div className="product-bulk-import-progress-info">
                  <span>Progreso: {importProgress.current} / {importProgress.total}</span>
                  <span>Exitosos: {importProgress.success} | Fallidos: {importProgress.failed}</span>
                </div>
                <div className="product-bulk-import-progress-bar">
                  <div
                    className="product-bulk-import-progress-fill"
                    style={{
                      width: `${(importProgress.current / importProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}

            {!importProgress && (
              <div className="product-bulk-import-actions">
                <button
                  onClick={handleClose}
                  disabled={isImporting}
                  className="product-bulk-import-btn product-bulk-import-btn-cancel"
                >
                  Cancelar
                </button>

                {validationResult && validationResult.validProducts && validationResult.validProducts.length > 0 && (
                  <button
                    onClick={handleImportProducts}
                    disabled={
                      isImporting ||
                      isValidating ||
                      (validationResult.errors && validationResult.errors.length > 0)
                    }
                    className="product-bulk-import-btn product-bulk-import-btn-success"
                  >
                    {isImporting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>Importando...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} />
                        <span>Importar {validationResult.validProducts.length} Producto(s)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductBulkImportModal;
